import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../redux/store';
import { fetchEvents } from '../../redux/eventsSlice';
import { useApiClient } from '../../ApiClientProvider';
import { Box, Typography, TextField, InputAdornment, CircularProgress, Container, Button, Popover, Skeleton, Chip, Checkbox } from '@mui/material';
import { Event, Search, ExpandMore } from '@mui/icons-material';
import TaskBoard from '../../components/Events/Management/Tasks/EventTaskBoard';
import { EventService } from '../../services/eventService';
import { IEventList } from '../../interfaces/IEventList';
import { EventTaskStatus } from '../../enums/EventTaskStatus';
import { EventStatus } from '../../enums/EventStatus';
import { FeatureKeys, useFeature } from '../../features';
import { CustomBreadcrumbs } from '../../components/custom-breadcrumbs';
//import { paths } from '../../routes/paths';
import FeatureLockedContent from '../../components/Features/FeatureLockedContent';

export default function TasksPage() {
    const dispatch = useDispatch<AppDispatch>();
    const apiClient = useApiClient('applicationClient');
    const eventService = useMemo(() => new EventService(apiClient), [apiClient]);
    const [rowsPerPage] = useState(10);
    const [page, setPage] = useState(0);

    const [searchParams] = useSearchParams();
    const [selectedEventIds, setSelectedEventIds] = useState<string[]>(() => {
        const param = searchParams.get('eventIds');
        return param ? param.split(',').filter(id => id.trim().length > 0) : [];
    });
    const [statusFilter, setStatusFilter] = useState<EventTaskStatus | 'all'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusCounts, setStatusCounts] = useState({
        all: 0, draft: 0, scheduled: 0, inProgress: 0, completed: 0, cancelled: 0,
    });

    // Pagination states
    const [loading, setLoading] = useState(false);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [allEvents, setAllEvents] = useState<IEventList[]>([]);

    // Event selector popover
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    // Refs for scroll detection
    const loadingRef = useRef(false);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastRequestRef = useRef<{ page: number; status: EventTaskStatus | 'all' } | null>(null);
    // Pre-mark as done when URL already supplies event IDs so auto-select doesn't override them
    const hasAutoSelectedRef = useRef<boolean>(
        !!new URLSearchParams(window.location.search).get('eventIds')
    );

    const taskManagementFeature = useFeature(FeatureKeys.TaskManagementEnabled);

    const resetPickerState = () => {
        setAllEvents([]);
        setPage(0);
        setHasMore(true);
        setIsFetchingMore(false);
        lastRequestRef.current = null;
        loadingRef.current = false;
    };

    const events = useMemo(() => {
        return allEvents.filter((evt: IEventList) =>
            evt.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [allEvents, searchTerm]);

    const getTaskTotal = (event: IEventList) => event.taskCounts.reduce((sum, task) => sum + task.count, 0);

    const formatEventRange = (event: IEventList) => {
        const start = dayjs(event.startDate);
        const end = dayjs(event.endDate);
        if (event.isSingleDay) return start.format('MMM D, YYYY');
        return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
    };

    const loadEvents = useCallback(async (pageNum: number, isNewSearch: boolean = false) => {
        // Prevent concurrent requests
        if (loadingRef.current) {
            console.debug('Load already in progress, skipping request');
            return;
        }

        // Prevent duplicate requests
        const currentStatusKey = statusFilter === 'all' ? 'all' : statusFilter;
        if (!isNewSearch && lastRequestRef.current?.page === pageNum && lastRequestRef.current?.status === currentStatusKey) {
            console.debug('Duplicate request detected, skipping');
            return;
        }

        loadingRef.current = true;
        setLoading(true);

        try {
            lastRequestRef.current = { page: pageNum, status: currentStatusKey };

            const result = await dispatch(fetchEvents({
                apiClient,
                status: statusFilter === 'all' ? undefined : statusFilter,
                page: pageNum + 1,
                pageSize: rowsPerPage,
            }));

            let newEvents: IEventList[] = [];

            if (
                result.payload &&
                typeof result.payload === 'object' &&
                'data' in result.payload &&
                Array.isArray((result.payload as { data: unknown }).data)
            ) {
                newEvents = (result.payload as { data: IEventList[] }).data;
            }

            // If we got less events than requested, we've reached the end
            setHasMore(newEvents.length === rowsPerPage);

            if (isNewSearch) {
                resetPickerState();
                setAllEvents(newEvents);
            } else {
                // Append new events for pagination
                setAllEvents(prev => [...prev, ...newEvents]);
            }
        } catch (err) {
            console.error('Failed to load events:', err);
            if (isNewSearch) {
                setAllEvents([]);
            }
        } finally {
            setLoading(false);
            setIsFetchingMore(false);
            loadingRef.current = false;
        }
    }, [statusFilter, apiClient, dispatch, rowsPerPage]);

    // Debounced scroll handler to prevent multiple rapid triggers
    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        if (loadingRef.current || !hasMore) return;

        // Clear existing timeout
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        const target = e.currentTarget;

        // Show loader immediately when near the bottom
        const { scrollTop, scrollHeight, clientHeight } = target;
        if (scrollHeight - scrollTop <= clientHeight + 100) {
            setIsFetchingMore(true);
        }

        // Debounce the actual page increment (100ms)
        scrollTimeoutRef.current = setTimeout(() => {
            const { scrollTop: st, scrollHeight: sh, clientHeight: ch } = target;
            if (sh - st <= ch + 100) {
                const nextPage = page + 1;
                setPage(nextPage);
                loadEvents(nextPage, false);
            }
        }, 100);
    }, [hasMore, loadEvents, page]);

    const handleUpdateEvent = async (eventId: string) => {
        const taskCounts = await eventService.getTaskCounts(eventId);
        setAllEvents(evts =>
            evts.map(e => e.id === eventId
                ? { ...e, taskCounts: taskCounts }
                : e
            )
        );
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            lastRequestRef.current = null;
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Initial load and when filters change
    useEffect(() => {
        const timer = window.setTimeout(() => {
            void loadEvents(0, true);
        }, 0);

        return () => window.clearTimeout(timer);
    }, [statusFilter, loadEvents]);

    // Load status counts
    useEffect(() => {
        const loadCounts = async () => {
            try {
                const counts = await eventService.getStatusCounts();
                setStatusCounts({
                    all: counts.total,
                    draft: counts.draft,
                    scheduled: counts.scheduled,
                    inProgress: counts.inProgress,
                    completed: counts.completed,
                    cancelled: counts.cancelled,
                });
            } catch (err) {
                console.error('Error loading status counts', err);
            }
        };
        loadCounts();
    }, [eventService]);

    // Auto-select the first event on initial load (runs once when events first arrive)
    useEffect(() => {
        if (!hasAutoSelectedRef.current && allEvents.length > 0) {
            hasAutoSelectedRef.current = true;
            setSelectedEventIds([allEvents[0].id]);
        }
    }, [allEvents]);

    if (taskManagementFeature.isLoading) {
        return null;
    }

    if (!taskManagementFeature.enabled) {
        return (
            <Container maxWidth="xl">
                <CustomBreadcrumbs
                    heading="Task Management "
                    sx={{ mb: { xs: 3, md: 3 } }}
                />
                <FeatureLockedContent
                    featureKey={FeatureKeys.TaskManagementEnabled}
                    title="Task Management"
                    description="Organise and track event tasks with a visual task board. Assign tasks to team members and monitor progress."
                    suggestedTier="Basic"
                />
            </Container>
        );
    }

    const statusOptions = [
        { value: 'all', label: `All events (${statusCounts.all})` },
        { value: 'draft', label: `Draft (${statusCounts.draft})` },
        { value: 'scheduled', label: `Scheduled (${statusCounts.scheduled})` },
        { value: 'inprogress', label: `In progress (${statusCounts.inProgress})` },
        { value: 'completed', label: `Completed (${statusCounts.completed})` },
        { value: 'cancelled', label: `Cancelled (${statusCounts.cancelled})` },
    ];

    const statusMeta = {
        [EventStatus.Draft]: { label: 'Draft', colour: '#6b7280' },
        [EventStatus.PendingApproval]: { label: 'Pending approval', colour: '#9ca3af' },
        [EventStatus.Scheduled]: { label: 'Scheduled', colour: '#2563eb' },
        [EventStatus.InProgress]: { label: 'In progress', colour: '#f59e0b' },
        [EventStatus.Postponed]: { label: 'Postponed', colour: '#8b5cf6' },
        [EventStatus.Cancelled]: { label: 'Cancelled', colour: '#ef4444' },
        [EventStatus.Completed]: { label: 'Completed', colour: '#10b981' },
        [EventStatus.Closed]: { label: 'Closed', colour: '#475569' },
    } as const;

    const filteredEventCount = events.length;
    const popoverOpen = Boolean(anchorEl);
    const popoverId = popoverOpen ? 'event-selector-popover' : undefined;

    const toggleEventSelection = (eventId: string) => {
        setSelectedEventIds(prev =>
            prev.includes(eventId)
                ? prev.filter(id => id !== eventId)
                : [...prev, eventId]
        );
    };

    const selectedCount = selectedEventIds.length;
    const buttonLabel = selectedCount === 0
        ? 'Select events'
        : selectedCount === 1
            ? (allEvents.find(e => e.id === selectedEventIds[0])?.title ?? '1 event selected')
            : `${selectedCount} events selected`;

    return (
        <Container maxWidth="xl">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <CustomBreadcrumbs
                    heading="Tasks"
                    sx={{ flex: 1, minWidth: 0 }}
                />

                <Button
                    aria-describedby={popoverId}
                    variant="outlined"
                    color="inherit"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    endIcon={<ExpandMore sx={{ color: 'text.secondary' }} />}
                    sx={{
                        textTransform: 'none',
                        borderColor: 'divider',
                        color: 'text.primary',
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.75,
                        gap: 1,
                        flexShrink: 0,
                        '&:hover': {
                            borderColor: 'text.secondary',
                            bgcolor: 'action.hover',
                        },
                    }}
                >
                    <Event sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, fontWeight: 600 }}>
                            {selectedCount > 1 ? 'Viewing events' : 'Tasks for'}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>
                            {buttonLabel}
                        </Typography>
                    </Box>
                </Button>

                <Popover
                    id={popoverId}
                    open={popoverOpen}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{
                        sx: {
                            mt: 0.75,
                            width: { xs: 'calc(100vw - 24px)', sm: 400 },
                            maxHeight: 540,
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: 8,
                            overflow: 'hidden',
                        },
                    }}
                >
                    {/* Header */}
                    <Box sx={{ p: 1.75, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Select events
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {selectedCount > 0 ? `${selectedCount} selected · ` : ''}{filteredEventCount} shown
                            </Typography>
                        </Box>

                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search events..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" color="action" />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />

                        <Box sx={{ display: 'flex', gap: 0.75, mt: 1.25, flexWrap: 'wrap' }}>
                            {statusOptions.map(option => {
                                const isActive = statusFilter === option.value;
                                return (
                                    <Chip
                                        key={option.value}
                                        label={option.label}
                                        onClick={() => setStatusFilter(option.value as EventTaskStatus | 'all')}
                                        size="small"
                                        variant={isActive ? 'filled' : 'outlined'}
                                        color={isActive ? 'primary' : 'default'}
                                        sx={{ fontWeight: isActive ? 700 : 500 }}
                                    />
                                );
                            })}
                        </Box>
                    </Box>

                    {/* Event list */}
                    <Box
                        onScroll={handleScroll}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            p: 1,
                            '&::-webkit-scrollbar': { width: '5px' },
                            '&::-webkit-scrollbar-track': { background: 'transparent' },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'divider',
                                borderRadius: '999px',
                            },
                        }}
                    >
                        {events.length === 0 && !loading ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, p: 3, textAlign: 'center', color: 'text.secondary' }}>
                                <Event sx={{ fontSize: 36, mb: 1, opacity: 0.25 }} />
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {searchTerm || statusFilter !== 'all' ? 'No matching events' : 'No events available'}
                                </Typography>
                            </Box>
                        ) : events.length === 0 && loading ? (
                            <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <Box key={i} sx={{ p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                                        <Skeleton variant="text" width="60%" height={20} />
                                        <Skeleton variant="text" width="80%" height={16} />
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {events.map(evt => {
                                    const isSelected = selectedEventIds.includes(evt.id);
                                    const meta = statusMeta[evt.status];
                                    const taskTotal = getTaskTotal(evt);

                                    return (
                                        <Box
                                            key={evt.id}
                                            onClick={() => {
                                                toggleEventSelection(evt.id);
                                            }}
                                            sx={{
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'stretch',
                                                gap: 1.25,
                                                p: 1.25,
                                                borderRadius: 1.5,
                                                border: '1px solid',
                                                borderColor: isSelected ? 'primary.main' : 'divider',
                                                bgcolor: isSelected ? 'action.selected' : 'transparent',
                                                transition: 'background-color 120ms, border-color 120ms',
                                                '&:hover': {
                                                    bgcolor: isSelected ? 'action.selected' : 'action.hover',
                                                },
                                            }}
                                        >
                                            {/* Status accent bar */}
                                            <Box sx={{ width: 3, borderRadius: 999, bgcolor: meta.colour, flexShrink: 0 }} />

                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {evt.title}
                                                    </Typography>
                                                    <Checkbox
                                                        checked={isSelected}
                                                        size="small"
                                                        sx={{ p: 0, flexShrink: 0 }}
                                                        onClick={e => e.stopPropagation()}
                                                        onChange={() => toggleEventSelection(evt.id)}
                                                        aria-label={`Select ${evt.title}`}
                                                    />
                                                </Box>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                                    {meta.label} &middot; {formatEventRange(evt)} &middot; {taskTotal} {taskTotal === 1 ? 'task' : 'tasks'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}

                                {isFetchingMore && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 1.5 }}>
                                        <CircularProgress size={16} thickness={4} />
                                        <Typography variant="caption" color="text.secondary">Loading…</Typography>
                                    </Box>
                                )}

                                {!isFetchingMore && !hasMore && events.length > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 1.25 }}>
                                        All events loaded
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                </Popover>
            </Box>

            {/* Task Board Content */}
            {selectedEventIds.length > 0 ? (
                <Box>
                    {loading && allEvents.length === 0 ? (
                        <Box>
                            <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2, mb: 2 }} />
                            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                        </Box>
                    ) : selectedEventIds.length === 1 ? (
                        <TaskBoard eventId={selectedEventIds[0]} onUpdateEvent={handleUpdateEvent} />
                    ) : (
                        <TaskBoard
                            eventIds={selectedEventIds}
                            eventOptions={selectedEventIds.map(id => ({
                                id,
                                title: allEvents.find(e => e.id === id)?.title ?? id,
                            }))}
                            onUpdateEvent={handleUpdateEvent}
                        />
                    )}
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'background.paper',
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        gap: 2,
                        minHeight: 300,
                        p: 4,
                    }}
                >
                    <Typography variant="h5" color="text.secondary" sx={{ mb: 1 }}>
                        Select an Event to Get Started
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
                        Choose an event from the dropdown to view and manage its tasks using the visual task board
                    </Typography>
                </Box>
            )}
        </Container>
    );
}